import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Download, Code2, Copy, Radar, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listStudents, studentFunnel, studentSources, studentsCsvUrl, widgetSettings, saveWidgetSettings, widgetWatch, runWidgetWatch, handoffStatus, weekReview } from "@/lib/studentsApi";
import { WeekReview } from "@/components/students/WeekReview";
import { FunnelCards } from "@/components/students/FunnelCards";
import { StudentsTable } from "@/components/students/StudentsTable";
import { SourceReport } from "@/components/students/SourceReport";

const EMBED_URL = `${process.env.REACT_APP_BACKEND_URL}/embed/subscribe`;
const POS = { 0: "Inserted at the top of the article, above the intro (plugin ≥ 1.7.0).", 1: "Inserted right after the intro paragraph — the earliest natural in-content position (plugin ≥ 1.7.0).", 2: "Inserted after the 2nd substantive paragraph.", 3: "Inserted after the 3rd substantive paragraph." };

function PlacementWatch() {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { widgetWatch().then(setW).catch(() => setW({ issues: [], results: [] })); }, []);
  const run = async () => { setBusy(true); try { setW(await runWidgetWatch()); toast.success("Placement check finished"); } catch { toast.error("Check failed"); } finally { setBusy(false); } };
  if (!w) return null;
  const bad = w.issues || [];
  return (
    <div className="rounded-xl border bg-card px-4 py-3 text-sm" data-testid="placement-watch">
      <div className="flex flex-wrap items-center gap-3">
        <Radar className={`h-4 w-4 ${bad.length ? "text-red-600" : "text-emerald-600"}`} />
        <div className="flex-1 min-w-[16rem]">
          <div className="font-medium">Daily placement watch · {w.checked_at ? `${w.ok}/${w.sample} sampled posts OK` : "not run yet"}</div>
          <div className="text-xs text-muted-foreground">Runs 02:45 UTC on 10 random live posts (plain HTTP, no AI): missing / duplicate widget, inserted inside a table or list, stray markup, wrong depth.{w.checked_at ? ` Last: ${new Date(w.checked_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""}</div>
        </div>
        <button data-testid="placement-watch-run" onClick={run} disabled={busy} className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-secondary disabled:opacity-50">{busy ? "Checking 10 posts…" : "Run now"}</button>
      </div>
      {bad.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs" data-testid="placement-watch-issues">
          {bad.map((i) => <li key={i.url} className="flex items-start gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" /><a href={i.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{i.title || i.url}</a><span className="text-muted-foreground">— {i.problems.join("; ")}</span><ExternalLink className="mt-0.5 h-3 w-3 text-muted-foreground" /></li>)}
        </ul>
      )}
    </div>
  );
}

function HandoffStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => { handoffStatus().then(setSt).catch(() => setSt(null)); }, []);
  if (!st) return null;
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm ${st.available ? "bg-emerald-50/50" : "bg-amber-50/60"}`} data-testid="handoff-status">
      <span className={`h-2.5 w-2.5 rounded-full ${st.available ? "bg-emerald-500" : "bg-amber-500"}`} />
      <div className="flex-1 min-w-[16rem]">
        <div className="font-medium">EntranceZone AI receiver: {st.available ? "live — students are handed off with ref+code" : "not deployed yet"}</div>
        <div className="text-xs text-muted-foreground">{st.available ? `${st.ai_url}/api/auth/ez-handoff answers (HTTP ${st.receiver_http}).` : `${st.ai_url}/api/auth/ez-handoff returns ${st.receiver_http ?? "no response"} — the widget shows a safe "Explore EntranceZone AI" link instead of the handoff button until the AI project ships the receiver (auto-detected every 5 min).`}</div>
      </div>
    </div>
  );
}

function WidgetRollout() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { widgetSettings().then(setCfg).catch(() => setCfg({ enabled: false, after_paragraph: 1, error: true })); }, []);
  const save = async (patch) => {
    try { setCfg(await saveWidgetSettings(patch)); toast.success(patch.enabled === false ? "Site-wide widget paused — live within 15 min" : "Saved — live on the site within 15 min"); }
    catch { toast.error("Could not save"); }
  };
  if (!cfg) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-card px-4 py-3 text-sm" data-testid="widget-rollout">
      <span className={`h-2.5 w-2.5 rounded-full ${cfg.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
      <div className="flex-1 min-w-[16rem]">
        <div className="font-medium">Site-wide widget on all posts (plugin ≥ 1.6.1)</div>
        <div className="text-xs text-muted-foreground">{POS[cfg.after_paragraph] || `Auto-inserted once per article after the ${cfg.after_paragraph}th substantive paragraph.`} Rule: visible as early as the article allows, never inside tables/lists, one per post. Permanent subscription feature — not part of the AI test.</div>
      </div>
      <label className="flex items-center gap-1.5 text-xs">Position
        <select data-testid="widget-paragraph" value={cfg.after_paragraph} onChange={(e) => save({ after_paragraph: Number(e.target.value) })} className="rounded-md border bg-background px-2 py-1">
          {[[0, "Top of article"], [1, "After intro (1st paragraph)"], [2, "After 2nd paragraph"], [3, "After 3rd paragraph"], [4, "After 4th paragraph"], [5, "After 5th paragraph"], [6, "After 6th paragraph"]].map(([n, l]) => <option key={n} value={n}>{l}</option>)}
        </select>
      </label>
      <button data-testid="widget-toggle" onClick={() => save({ enabled: !cfg.enabled })}
        className={`rounded-lg px-3 py-1.5 text-xs font-medium ${cfg.enabled ? "border hover:bg-secondary" : "bg-primary text-primary-foreground"}`}>
        {cfg.enabled ? "Pause site-wide" : "Enable site-wide"}
      </button>
    </div>
  );
}

function EmbedSnippets() {
  const [open, setOpen] = useState(false);
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const iframe = `<iframe src="${EMBED_URL}?exam=JEE%20Main" style="width:100%;border:0;min-height:520px" loading="lazy" title="EntranceZone updates"></iframe>`;
  return (
    <div className="rounded-xl border bg-card" data-testid="embed-snippets">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium" data-testid="embed-snippets-toggle">
        <Code2 className="h-4 w-4" /> Embed the subscription widget on WordPress
      </button>
      {open && (
        <div className="space-y-3 border-t px-4 py-3 text-xs">
          <p className="text-muted-foreground">Plugin v1.6.1 auto-inserts the widget on every post (toggle above). Use the shortcode only to place it manually — the auto-insert then skips that post. <code>exam</code> pre-selects an exam (defaults to the post title's exam), <code>cta</code> customises the AI button, <code>compact="1"</code> for sidebars.</p>
          {[["[ez_subscribe]", "shortcode-basic"], ['[ez_subscribe exam="NEET UG" cta="Predict my NEET rank"]', "shortcode-exam"], ['[ez_subscribe compact="1"]', "shortcode-compact"]].map(([s, t]) => (
            <div key={t} className="flex items-center gap-2"><code className="flex-1 rounded-md bg-secondary px-2 py-1.5 font-mono" data-testid={t}>{s}</code><button onClick={() => copy(s)} className="rounded-md border p-1.5 hover:bg-secondary"><Copy className="h-3.5 w-3.5" /></button></div>
          ))}
          <div className="text-muted-foreground">Plain iframe (any site):</div>
          <div className="flex items-start gap-2"><code className="flex-1 break-all rounded-md bg-secondary px-2 py-1.5 font-mono" data-testid="embed-iframe">{iframe}</code><button onClick={() => copy(iframe)} className="rounded-md border p-1.5 hover:bg-secondary"><Copy className="h-3.5 w-3.5" /></button></div>
          <a href={`${EMBED_URL}?exam=JEE%20Main`} target="_blank" rel="noreferrer" className="inline-block text-indigo-700 hover:underline" data-testid="embed-preview-link">Open widget preview ↗</a>
        </div>
      )}
    </div>
  );
}

export default function Students() {
  const { user } = useAuth();
  const authed = !!user?.permissions?.manage;
  const [days, setDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [q, setQ] = useState("");
  const [ai, setAi] = useState("");
  const [env, setEnv] = useState("PRODUCTION");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    if (!authed) return;
    try {
      const [f, l, s] = await Promise.all([studentFunnel(days), listStudents({ q, ai, env, limit: LIMIT, skip: page * LIMIT }), studentSources(days)]);
      setFunnel(f); setData(l); setSources(s);
    } catch { toast.error("Could not load students"); }
  }, [authed, days, q, ai, env, page]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);

  if (user && !authed) return <Navigate to="/" replace />;
  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Users className="h-6 w-6 text-primary" /> Students</h1>
          <p className="text-sm text-muted-foreground mt-1">Subscription → EntranceZone AI funnel. Editorial Intelligence is the source of truth for subscription, exams and WhatsApp consent; EntranceZone AI owns the account.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center rounded-lg border p-0.5 font-medium">
            {[7, 30, 90].map((d) => <button key={d} data-testid={`students-range-${d}`} onClick={() => setDays(d)} className={`rounded-md px-2.5 py-1 transition-colors ${days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{d}d</button>)}
          </div>
          <a href={studentsCsvUrl()} data-testid="students-export" className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 hover:bg-secondary"><Download className="h-3.5 w-3.5" /> Export CSV</a>
        </div>
      </div>

      <WeekReview load={weekReview} />
      <HandoffStatus />
      <FunnelCards data={funnel} />
      <SourceReport data={sources} />
      <WidgetRollout />
      <PlacementWatch />
      <EmbedSnippets />

      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="students-filters">
        <input data-testid="students-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search name, email, WhatsApp, ID…" className="w-64 rounded-md border bg-background px-2.5 py-1.5" />
        <select data-testid="students-ai-filter" value={ai} onChange={(e) => { setAi(e.target.value); setPage(0); }} className="rounded-md border bg-background px-2 py-1.5">
          <option value="">All students</option><option value="connected">AI connected</option><option value="not_connected">Not connected</option>
        </select>
        <select data-testid="students-env-filter" value={env} onChange={(e) => { setEnv(e.target.value); setPage(0); }} className="rounded-md border bg-background px-2 py-1.5" title="Production metrics above always exclude TEST records">
          <option value="PRODUCTION">Production</option><option value="TEST">Test</option><option value="ALL">All</option>
        </select>
        {data && <span className="text-muted-foreground" data-testid="students-count">{data.total} students · page {page + 1} of {pages}</span>}
        <div className="ml-auto flex gap-1">
          <button disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-md border px-2 py-1 disabled:opacity-40" data-testid="students-prev">Prev</button>
          <button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="rounded-md border px-2 py-1 disabled:opacity-40" data-testid="students-next">Next</button>
        </div>
      </div>
      <StudentsTable data={data} />
    </div>
  );
}
